import type { InternalPermissionGroup } from 'src/interfaces/internalAccess'
import { InternalPermission } from './internalPermissions'

/**
 * Labels are the section comments in the backend enum, so a reader can match a group to its
 * source. Order is the backend's order.
 */
export const INTERNAL_PERMISSION_GROUPS: InternalPermissionGroup[] = [
  {
    label: 'Internal admin',
    permissions: [
      InternalPermission.InternalUsersView,
      InternalPermission.InternalUsersManage,
      InternalPermission.InternalRolesView,
      InternalPermission.InternalRolesManage,
      InternalPermission.InternalAuditLogsView,
    ],
  },
  {
    label: 'Partners',
    permissions: [
      InternalPermission.PartnersView,
      InternalPermission.PartnersCreate,
      InternalPermission.PartnersLoginAs,
      InternalPermission.PartnerSettingsView,
      InternalPermission.PartnerSettingsManage,
      InternalPermission.PartnerProductsView,
      InternalPermission.PartnerProductsManage,
      InternalPermission.PartnerOnboardMasterContractor,
      InternalPermission.PartnersDelete,
      InternalPermission.PartnerBillingView,
      InternalPermission.PartnerBillingManage,
    ],
  },
  {
    label: 'Organizations',
    permissions: [InternalPermission.OrganizationsView, InternalPermission.OrganizationsManage],
  },
  {
    label: 'Customer users',
    permissions: [
      InternalPermission.CustomerUsersView,
      InternalPermission.CustomerUsersManage,
      InternalPermission.CustomerUserAccessUpdate,
      InternalPermission.CustomerInvitesView,
      InternalPermission.CustomerInvitesManage,
      InternalPermission.CustomerRolesView,
      InternalPermission.CustomerRolesManage,
      InternalPermission.CustomerRolesDelete,
    ],
  },
  {
    label: 'Markets',
    permissions: [InternalPermission.MarketsView, InternalPermission.MarketsManage],
  },
  {
    label: 'Onboarding configuration',
    permissions: [
      InternalPermission.CollectDataTemplatesView,
      InternalPermission.CollectDataTemplatesManage,
      InternalPermission.DocusignTemplatesView,
      InternalPermission.DocusignTemplatesManage,
    ],
  },
  {
    label: 'Contractors',
    permissions: [
      InternalPermission.ContractorsView,
      InternalPermission.ContractorsManage,
      InternalPermission.ContractorTemplateChange,
      InternalPermission.ContractorOnboardingStatusManage,
      InternalPermission.ContractorStepsManage,
      InternalPermission.ContractorDocumentsManage,
      InternalPermission.ContractorDataDelete,
    ],
  },
  {
    label: 'Document review',
    permissions: [
      InternalPermission.DocumentReviewView,
      InternalPermission.DocumentReviewApprove,
      InternalPermission.DocumentReviewRecollect,
    ],
  },
  {
    label: 'MC document review',
    permissions: [
      InternalPermission.McDocumentReviewView,
      InternalPermission.McDocumentReviewApprove,
      InternalPermission.McDocumentReviewReject,
    ],
  },
  {
    label: 'Insurance',
    permissions: [
      InternalPermission.InsuranceProductsView,
      InternalPermission.InsuranceProductsManage,
      InternalPermission.InsurancePoliciesView,
      InternalPermission.InsurancePoliciesManage,
      InternalPermission.InsuranceContractsUpload,
    ],
  },
  {
    label: 'AI document review',
    permissions: [
      InternalPermission.AiDocumentReviewConfigsView,
      InternalPermission.AiDocumentReviewConfigsManage,
    ],
  },
  {
    label: 'LMS training',
    permissions: [InternalPermission.LmsTrainingsView, InternalPermission.LmsTrainingsManage],
  },
  {
    label: 'Payout configuration',
    permissions: [InternalPermission.PayoutSettingsView, InternalPermission.PayoutSettingsManage],
  },
  {
    label: 'Payments operations',
    permissions: [
      InternalPermission.PaymentsReviewView,
      InternalPermission.PaymentsReviewDecide,
      InternalPermission.PaymentsReviewBulkApprove,
      InternalPermission.TransactionsReviewView,
      InternalPermission.TransactionsAssignInvoice,
      InternalPermission.TransactionsAuthorize,
      InternalPermission.RefundsView,
      InternalPermission.RefundsInitiate,
      InternalPermission.RefundsExecute,
      InternalPermission.ReconciliationRun,
      InternalPermission.WalletsView,
      InternalPermission.FeeWithdrawalsView,
      InternalPermission.UsBankTransactionsView,
      InternalPermission.BankOnboardingView,
      InternalPermission.BankOnboardingManage,
      InternalPermission.UsBankKillswitchManage,
      InternalPermission.UsBankCapturedEventsReplay,
    ],
  },
  {
    label: 'Invoices and funding',
    permissions: [
      InternalPermission.InvoicesView,
      InternalPermission.InvoicesManage,
      InternalPermission.HistoricalInvoicesUpload,
    ],
  },
  {
    label: 'Integrations',
    permissions: [
      InternalPermission.YardstikView,
      InternalPermission.YardstikManage,
      InternalPermission.OatfiView,
      InternalPermission.OatfiManage,
      InternalPermission.PlaidLinkGenerate,
    ],
  },
  {
    label: 'OAuth clients and webhooks',
    permissions: [
      InternalPermission.OauthClientsView,
      InternalPermission.OauthClientsManage,
      InternalPermission.OauthTokensRevoke,
      InternalPermission.WebhookSubscriptionsView,
      InternalPermission.WebhookSubscriptionsManage,
      InternalPermission.WebhookDeliveriesView,
      InternalPermission.WebhooksSend,
    ],
  },
  {
    label: 'Feature flags',
    permissions: [InternalPermission.FeatureFlagsView, InternalPermission.FeatureFlagsManage],
  },
  {
    label: 'Maintenance jobs',
    permissions: [InternalPermission.MaintenanceJobsRun],
  },
  {
    label: 'Schedulers',
    permissions: [
      InternalPermission.SchedulersView,
      InternalPermission.SchedulersManage,
      InternalPermission.SchedulerTemplatesManage,
    ],
  },
  {
    label: 'Remote config',
    permissions: [InternalPermission.RemoteConfigView, InternalPermission.RemoteConfigManage],
  },
]
